/* eslint-disable no-extra-parens */
import React, { Component } from 'react';

import pollenAttributionImage from './assets/powered_by_pollen.png';
import airQualityByAmbeeAttributionImage from './assets/air_quality_by_ambee.png';
import PropTypes from 'prop-types';
import { dataLayer, utilities } from '@weatherbug/common';
import * as constants from './constants';
import { convertUnixTimeStampToDate } from '../utilities/dateTimeUtils';

const { unitSystems, pressureUnits, pressureUnitLongAbbreviations, temperatureUnits, lengthUnitsLongAbbreviations, temperatureUnitAbbreviations, distanceUnits, lengthUnits, speedUnits, speedUnitsEnum, distanceUnitAbbreviations } = utilities.units;

const dateTimeDisplayFormat = 'h:mm a';
const dateDisplayFormat = 'MM/dd/yy';
const DEFAULT_LAYER_OPACITY = 0.7;
const windSpeedLayerId = 'Contour.Observed.WindSpeed';
const tempLocalLayerIdMetric = 'Observed.Temperature';
const tempLocalLayerIdEnglish = `${tempLocalLayerIdMetric}.English`;
const pressureLocalLayerIdMetric = 'Observed.Pressure.SeaLevel';
const pressureLocalLayerIdEnglish = `${pressureLocalLayerIdMetric}.English`;
const unitSupportedLayerIds = [
    'Contour.Observed.DailyRain',
    'Contour.Observed.Temperature',
    'Observed.Temperature',
    'Observed.Temperature.English',
    'Contour.Observed.Pressure.SeaLevel',
    'Observed.Pressure.SeaLevel',
    'Observed.Pressure.SeaLevel.English',
    'Contour.Observed.Temperature.HeatIndex',
    'Contour.Observed.WindChill',
    'Contour.Observed.WindSpeed',
    'Contour.Observed.DewPoint'
];
//TECHDEBT: you must wrap this component in the extension with map && to ensure that the map is instantiated before this mounts.  We should fix that so it is handled in this class automatically if possible.
export class RasterLayer extends Component {
    static propTypes = {
        map: PropTypes.object.isRequired, // This should be a ref to an instantiated mapbox map object on the page
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        mapBaseUrl: PropTypes.string.isRequired,
        tileBaseUrl: PropTypes.string.isRequired,
        authId: PropTypes.string.isRequired,
        secret: PropTypes.string.isRequired,
        stdApiParameters: PropTypes.object,
        layerOpacity: PropTypes.number,
        tempUnit: PropTypes.number,
        pressureUnit: PropTypes.number,
        rainUnit: PropTypes.number,
        windUnit: PropTypes.number,
        defaultLayerId: PropTypes.string,
        zipCode: PropTypes.number,
        pollenAttributionUrl: PropTypes.string,
        airQualityAttributionUrl: PropTypes.string,
        onLayerChanged: PropTypes.func,
        useDynamicSharding: PropTypes.bool,
        showAttributionIcon: PropTypes.bool,
        adRefreshActionOnAnimationStart: PropTypes.func,
        adRefreshActionOnAnimationStop: PropTypes.func,
        adRefreshActionOnLayerChange: PropTypes.func,
        adRefreshActionOnScrollWheelZoom: PropTypes.func,
        adRefreshActionOnDoubleClickZoomIn: PropTypes.func
    };

    static defaultProps = {
        airQualityAttributionUrl: 'https://www.getambee.com',
        tempUnit: temperatureUnits.fahrenheit,
        pressureUnit: pressureUnits.inches,
        rainUnit: lengthUnits.inches,
        windUnit: speedUnits.mph,
        useDynamicSharding: true,
        showAttributionIcon: true
    };

    _isMounted = false;

    constructor(props) {
        super(props);
        this.state = {
            shouldShowLegends: true,
            legendLabels: [],
            legendColors: [],
            alertsLegend: [],
            animating: false,
            animationTimeslots: [],
            currentTimeslotIndex: 0,
            mapLayers: [],
            currentLayerId: '',
            currentVisibleLayerId: null,
            currentlyAnimatingLayerId: null,
            isAnimationPaused: false
        };

        this.changeLayer = this.changeLayer.bind(this);
        this.animationClick = this.animationClick.bind(this);
        this.onPanned = this.onPanned.bind(this);
        this.onZoomChanged = this.onZoomChanged.bind(this);
        this.setupMapProvider = this.setupMapProvider.bind(this);
        this.getMapMetadata = this.getMapMetadata.bind(this);
        this.checkForLegendUpdate = this.checkForLegendUpdate.bind(this);
        this.addMapLayer = this.addMapLayer.bind(this);
        this.createTileUrl = this.createTileUrl.bind(this);
        this.getTilesForAnimation = this.getTilesForAnimation.bind(this);
        this.addMapLayers = this.addMapLayers.bind(this);
        this.getMapLayers = this.getMapLayers.bind(this);
        this.animate = this.animate.bind(this);
        this.getMapLegend = this.getMapLegend.bind(this);
    }

    setupMapProvider(mapBaseUrl, authId, secret) {
        this.mapProvider = new dataLayer.providers.MapProvider(mapBaseUrl, authId, secret);
    }

    async componentDidMount() {
        this._isMounted = true;
        const {
            mapBaseUrl,
            authId,
            secret,
            defaultLayerId,
            latitude,
            longitude,
            tempUnit,
            pressureUnit,
            stdApiParameters,
            map,
            adRefreshActionOnScrollWheelZoom,
            adRefreshActionOnDoubleClickZoomIn
        } = this.props;

        if (mapBaseUrl && authId && secret) {
            this.setupMapProvider(mapBaseUrl, authId, secret);
        }

        if (this.mapProvider && Number.isFinite(latitude) && Number.isFinite(longitude) && map) {
            try {
                let currentLayerId = this.state.currentLayerId;
                if (!this.layersLoaded) {
                    currentLayerId = await this.getMapLayers(
                        latitude,
                        longitude,
                        stdApiParameters,
                        defaultLayerId,
                        tempUnit,
                        pressureUnit
                    );
                }

                if (currentLayerId) {
                    let mapMetadata = await this.getMapMetadata(currentLayerId, stdApiParameters);

                    if (mapMetadata) {
                        await this.getMapLegend(currentLayerId, mapMetadata, stdApiParameters);   
                    }   
                }
            } catch (error) {
                console.log(error);
            }
        }

        map?.on('dragend', this.onPanned);
        map?.on('zoomend', this.onZoomChanged);

        map.on('wheel', () => {
            const zoomLevel = map?.getZoom();
            adRefreshActionOnScrollWheelZoom?.(zoomLevel);
        });

        map.on('dblclick', () => {
            const zoomLevel = map?.getZoom();
            adRefreshActionOnDoubleClickZoomIn?.(zoomLevel);
        });
    }

    onPanned() {
        this.checkForLegendUpdate();
    }

    onZoomChanged() {
        this.checkForLegendUpdate();
    }

    async checkForLegendUpdate() {
        const { currentLayerId, mapMetadata } = this.state;
        if (currentLayerId === 'dta:nws' && mapMetadata) {
            await this.getMapLegend(currentLayerId, mapMetadata, this.props.stdApiParameters);
        }
    }

    async componentDidUpdate(prevProps) {
        const {
            mapBaseUrl,
            authId,
            secret,
            map,
            defaultLayerId,
            tempUnit,
            pressureUnit,
            windUnit,
            stdApiParameters,
            latitude,
            longitude,
            layerOpacity
        } = this.props;
        if (mapBaseUrl && authId && secret && !this.mapProvider) {
            this.setupMapProvider(mapBaseUrl, authId, secret);
        }
        const prevLatitude = prevProps.latitude;
        const prevLongitude = prevProps.longitude;

        const tempUnitChanged = Number.isInteger(tempUnit) && Number.isInteger(prevProps.tempUnit) && tempUnit !== prevProps.tempUnit;
        const pressureUnitChanged = Number.isInteger(pressureUnit) && Number.isInteger(prevProps.pressureUnit) && pressureUnit !== prevProps.pressureUnit;
        const windUnitChanged = Number.isInteger(windUnit) && Number.isInteger(prevProps.windUnit) && windUnit !== prevProps.windUnit;

        const unitsChanged = tempUnitChanged || pressureUnitChanged || windUnitChanged;
        const locationChanged = !isNaN(latitude) && latitude !== prevLatitude
            || !isNaN(longitude) && longitude !== prevLongitude;

        if (this.mapProvider) {
            if ((locationChanged || unitsChanged || !this.layersLoaded) && Number.isFinite(latitude) && Number.isFinite(longitude)) {
                await this.getMapLayers(
                    latitude,
                    longitude,
                    stdApiParameters,
                    defaultLayerId,
                    tempUnit,
                    pressureUnit
                );
            }

            let mapMetadata;
            if (locationChanged || this.state.currentLayerId !== this.mapMetadataLayerId && this.state.currentLayerId) {
                mapMetadata = await this.getMapMetadata(this.state.currentLayerId, stdApiParameters);
            }

            if ((mapMetadata || this.state.mapMetadata) && this.state.currentLayerId && this.mapLegendLayerId !== this.state.currentLayerId) {
                mapMetadata = mapMetadata || this.state.mapMetadata;
                await this.getMapLegend(this.state.currentLayerId, mapMetadata, stdApiParameters);
            }

            if (unitsChanged 
                && this.state.currentLayerId 
                && this.state.currentLayerId === this.mapMetadataLayerId 
                && unitSupportedLayerIds.includes(this.state.currentLayerId)) {
                let unitBasedLayerId = this.state.currentLayerId;
                let shouldReplaceLayer = false;
                // Local Temperature Layer is a little problematic since we are using the same map layer to draw this.
                // this approach will ensure layer that is drawn a given is unit is replaced when units changes without having to refresh/reload page
                // Observed.Temperature.English is Id when temperature unit is Fahrenheit, Observed.Temperature is layer Id when temperature unit is Celsius
                if (tempUnitChanged && tempUnit && this.state.currentLayerId === tempLocalLayerIdMetric || this.state.currentLayerId === tempLocalLayerIdEnglish) {
                    unitBasedLayerId = tempUnit === temperatureUnits.celsius ? tempLocalLayerIdMetric : tempLocalLayerIdEnglish;
                    shouldReplaceLayer = true;
                }
                // Local Pressure Layer also problematic similar issue as obsevered on Local Temperature Layer
                // Observed.Pressure.SeaLevel.English is Id when pressure unit is inHg, Observed.Pressure.SeaLevel is layer Id when pressure unit is millibars (mbar)
                if (pressureUnitChanged && pressureUnit && this.state.currentLayerId === pressureLocalLayerIdMetric || this.state.currentLayerId === pressureLocalLayerIdEnglish) {
                    shouldReplaceLayer = true;
                    unitBasedLayerId = pressureUnit === pressureUnits.millibars ? pressureLocalLayerIdMetric : pressureLocalLayerIdEnglish;
                }

                if (windUnitChanged && windUnit && this.state.currentLayerId === windSpeedLayerId) {
                    shouldReplaceLayer = false;
                    unitBasedLayerId = this.state.currentLayerId;
                }

                if (shouldReplaceLayer && unitBasedLayerId) {
                    this.changeLayer(unitBasedLayerId);
                } else {
                    const mapMetadata = await this.getMapMetadata(unitBasedLayerId, stdApiParameters);
                    await this.getMapLegend(unitBasedLayerId, mapMetadata, stdApiParameters);
                    
                    this.setState({
                        currentLayerId: unitBasedLayerId
                    });   
                    
                }
            }
        }
        const oldLayerOpacity = prevProps.layerOpacity || DEFAULT_LAYER_OPACITY;

        if (oldLayerOpacity !== layerOpacity && this.state.currentVisibleLayerId) {
            const newLayerOpacity = !isNaN(layerOpacity) ? layerOpacity / 100 : oldLayerOpacity;
            if (map?.getStyle?.()?.layers?.some(layer => layer?.id === this.state.currentVisibleLayerId)) {
                if (map?.getLayer(this.state.currentVisibleLayerId)) {
                    map?.setPaintProperty(this.state.currentVisibleLayerId, 'raster-opacity', newLayerOpacity);
                }   
            }
        }
    }

    componentWillUnmount() {
        const { map } = this.props;

        map?.off('load', this.onMapLoadListener);
        this._isMounted = false;
    }

    addMapLayer(id, layerId, tileUrl, isVisibleLayer) {
        const { map, layerOpacity, useDynamicSharding } = this.props;
        const addLayer = (map, id, layerId, tileUrl, isVisibleLayer) => {
            if (!map) {
                return;
            }
            const sourceId = id + 'source';

            if (map?.getSource?.(sourceId)) {
                return;
            }

            const currentLayerOpacity = !isNaN(layerOpacity) ? layerOpacity / 100 : DEFAULT_LAYER_OPACITY;
            let tileUrlArray = [tileUrl];
            if (tileUrl && useDynamicSharding && tileUrl?.includes?.('https://')) {
                const urlParts = tileUrl.split('https://');
                tileUrlArray = [
                    `https://a${urlParts[1]}`,
                    `https://b${urlParts[1]}`,
                    `https://c${urlParts[1]}`,
                    `https://d${urlParts[1]}`
                ];
            }

            map?.addSource?.(sourceId, {
                type: 'raster',
                tiles: tileUrlArray,
                scheme: 'xyz',
                tileSize: 512
            });
            const currentLayer = {
                id,
                type: 'raster',
                slot: 'top',
                source: sourceId,
                'source-layer': id,
                paint: {
                    'raster-opacity': isVisibleLayer ? currentLayerOpacity : 0
                }
            };
            
            map?.addLayer?.(currentLayer);
            this.setState({ currentLayer });   
            
        };

        if (map?._loaded) {
            addLayer(map, id, layerId, tileUrl, isVisibleLayer);
        } else {
            this.onMapLoadListener = map?.on?.('load', () => {
                addLayer(map, id, layerId, tileUrl, isVisibleLayer);
            });
        }
        
        const { mapLayers } = this.state;

        if (!mapLayers.find(x => x === id)) {
            mapLayers.push(id);
        }

        if (isVisibleLayer) {
            this.setState({ currentVisibleLayerId: id });   
        }
    }

    async changeLayer(layerId) {
        const { map, stdApiParameters, onLayerChanged } = this.props;
        const { currentLayerId, mapLayers } = this.state;
        if (layerId !== currentLayerId || !this.state.mapMetadata) {
            if (!map) {
                return;
            }

            if (currentLayerId && mapLayers) {
                mapLayers.forEach(layer => {
                    if (map?.getStyle?.()?.layers?.some(existingMapLayer => existingMapLayer?.id === layer)) {
                        if (map.getLayer(layer)) {
                            map.removeLayer(layer);
                        }

                        const sourceId = layer + 'source';
                        if (map.getSource(sourceId)) {
                            map.removeSource(sourceId);
                        }
                    }
                });
            }
            
            this.setState({
                currentLayerId: layerId,
                animationTime: null,
                currentVisibleLayerId: null,
                mapLayers: [],
                isAnimationPaused: false
            }, async () => {
                const mapMetadata = await this.getMapMetadata(layerId, stdApiParameters);
    
                //This has to wait for the map metadata because the alert layer api call requires the metadata
                await this.getMapLegend(layerId, mapMetadata, stdApiParameters);
    
                //Stop animating if we switch layers while animating.
                if (this.state.animating) {
                    this.animationClick();
                }
    
                if (onLayerChanged) {
                    onLayerChanged(layerId);
                }
            });   
            
        }
    }

    async getMapLayers(latitude, longitude, stdApiParameters, defaultLayerId, tempUnit, pressureUnit) {
        const apiResult = await this.mapProvider.getMapLayers(
            latitude,
            longitude,
            stdApiParameters,
            tempUnit || temperatureUnits.fahrenheit,
            pressureUnit || pressureUnits.inches
        );
        const layers = apiResult?.layers;
        const currentLayerId = this.state.currentLayerId || defaultLayerId || apiResult.defaultLayer;
        
        if (!this._isMounted) {
            return;
        }
        this.layersLoaded = true;
        this.setState({ layers, currentLayerId });   
        
        return currentLayerId;
    }

    async getMapMetadata(currentLayerId, stdApiParameters) {
        const mapMetadata = await this.mapProvider.getMapMetadata(currentLayerId, stdApiParameters, true);

        if (!this._isMounted) {
            return;
        }

        this.mapMetadataLayerId = currentLayerId;

        let animationTimeslots = [];

        if (mapMetadata?.canAnimate) {
            animationTimeslots = mapMetadata?.animation?.slots;
        }

        this.addMapLayers(currentLayerId, mapMetadata);
        
        this.setState({ mapMetadata, animationTimeslots });   
        
        return mapMetadata;
    }

    createTileUrl(timeSlotId, currentLayerId, mapMetadata) {
        const timestamp = mapMetadata?.timestamp;
        const { tileBaseUrl } = this.props;
        return `${tileBaseUrl}/maps/overlays/tile?lid=${currentLayerId}&espg=3857&tileSize=512&ts=${timestamp}&t=${timeSlotId}&x={x}&y={y}&z={z}&token=${mapMetadata?.token}`;
    }

    addMapLayers(currentLayerId, mapMetadata) {
        const defaultAnimationTimestamp = mapMetadata?.defaultSlot?.id;
        const tileUrl = this.createTileUrl(defaultAnimationTimestamp, currentLayerId, mapMetadata);
        this.addMapLayer(currentLayerId + defaultAnimationTimestamp, currentLayerId, tileUrl, true);
    }

    getTilesForAnimation() {
        const { currentLayerId, mapMetadata } = this.state;
        const defaultAnimationTimestamp = mapMetadata?.defaultSlot?.id;

        mapMetadata?.animation?.slots?.forEach(timeslot => {
            const tileUrl = this.createTileUrl(timeslot?.id, currentLayerId, mapMetadata);
            const id = currentLayerId + timeslot?.id;
            const isVisibleLayer = timeslot?.id === defaultAnimationTimestamp;

            this.addMapLayer(id, currentLayerId, tileUrl, isVisibleLayer);
        });
    }

    animationClick() {
        const { mapMetadata, currentLayerId, currentlyAnimatingLayerId, currentTimeslotIndex, animationTime  } = this.state;
        const { adRefreshActionOnAnimationStart, adRefreshActionOnAnimationStop } = this.props;

        const animating = !this.state.animating;

        if (animating) {
            this.getTilesForAnimation();
            adRefreshActionOnAnimationStart?.();
            const animationLength = mapMetadata?.animation?.interval * 1000 || 1000;
            this.animate();
            this.animationTimer = setInterval(() => {
                this.animate();
            }, animationLength);
            this.setState({ currentlyAnimatingLayerId: currentLayerId, isAnimationPaused: false });
        } else {
            if (this.animationTimer) {
                adRefreshActionOnAnimationStop?.();
                clearInterval(this.animationTimer);
            }

            if (currentlyAnimatingLayerId && currentlyAnimatingLayerId === currentLayerId) {
                if (!this.state.isAnimationPaused) {
                    this.setState({ isAnimationPaused: true });
                }
            } else {
                this.setState({ animationTime: null, currentTimeslotIndex: 0, currentlyAnimatingLayerId: null });
            }
            
        }

        this.setState({ animating });
    }

    animate() {
        const { map, layerOpacity, adRefreshActionCycleSevenSlotsAnimation } = this.props;
        const currentLayerOpacity = !isNaN(layerOpacity) ? layerOpacity / 100 : DEFAULT_LAYER_OPACITY;
        const {
            currentTimeslotIndex,
            animationTime,
            animationTimeslots,
            currentLayerId,
            currentVisibleLayerId
        } = this.state;

        let newAnimationTimeslot;
        let newTimeslotIndex;
        if (!animationTime || currentTimeslotIndex + 1 >= this.state.animationTimeslots.length) {
            newAnimationTimeslot = animationTimeslots[0];
            newTimeslotIndex = 0;
        } else {
            newAnimationTimeslot = animationTimeslots[currentTimeslotIndex + 1];
            newTimeslotIndex = currentTimeslotIndex + 1;
        }

        if (newTimeslotIndex === 6) {
            adRefreshActionCycleSevenSlotsAnimation?.();
        }

        if (newAnimationTimeslot) {
            // convert newAnimationTimeslot.time Timestamp in seconds into a formatted string representation of time h:mm a => 2:55 PM, 
            // call toLowerCase() since convertUnixTimeStampToDate returns time with AM/PM in Uppercase.
            const timestampInMilliseconds = Number.isFinite(newAnimationTimeslot?.time)  ? newAnimationTimeslot.time * 1000 : null;
            const newAnimationTime = Number.isFinite(timestampInMilliseconds)
                ? convertUnixTimeStampToDate(timestampInMilliseconds, dateTimeDisplayFormat)?.toLowerCase()
                : '--';
            
            this.setState({
                currentTimeslotIndex: newTimeslotIndex,
                animationTime: newAnimationTime
            });

            if (map?.getStyle?.()?.layers?.some(layer => layer?.id === currentVisibleLayerId)) {
                if (map?.getLayer(currentVisibleLayerId)) {
                    map?.setPaintProperty(currentVisibleLayerId, 'raster-opacity', 0);
                }
            }

            const newVisibleLayerId = currentLayerId + newAnimationTimeslot.id;
            if (map?.getStyle?.()?.layers?.some(layer => layer?.id === newVisibleLayerId)) {
                if (map?.getLayer(newVisibleLayerId)) {
                    map?.setPaintProperty(newVisibleLayerId, 'raster-opacity', currentLayerOpacity);
                    this.setState({ currentVisibleLayerId: newVisibleLayerId });
                }
            }
        }
    }

    async getMapLegend(currentLayerId, mapMetadata, stdApiParameters) {
        const { map, tempUnit, pressureUnit, rainUnit, windUnit } = this.props;
        if (currentLayerId === 'dta:nws' && mapMetadata && mapMetadata.defaultSlot) {
            const mapCanvas = map?.getCanvas();
            const mapCenter = map?.getCenter();
            const animationTimestamp = mapMetadata && mapMetadata.defaultSlot && mapMetadata.defaultSlot.id;
            const zoomLevel = map?.getZoom();
            const apiResult = await this.mapProvider.getAlertsLegend(
                mapCanvas.width,
                mapCanvas.height,
                null,
                null,
                null,
                null,
                mapCenter.lat,
                mapCenter.lng,
                animationTimestamp,
                zoomLevel,
                stdApiParameters
            );
            this.mapLegendLayerId = currentLayerId;
            
            this.setState({
                alertsLegend: apiResult.val,
                legendData: undefined,
                legendLabels: [],
                legendColors: [],
                legendSets: []
            });

        } else {
            const apiResult = await this.mapProvider.getLayerLegend(currentLayerId, stdApiParameters);
            this.mapLegendLayerId = currentLayerId;
            const mapLegend = apiResult?.val ?? {};
            let legendLabels = [];
            let legendColors = [];
            let legendSets;
            let legendUnits;
            if (mapLegend.layerComponents.length > 0) {
                //TECHDEBT: I think lot of this should be handled on the API side of things.
                let currentLayerComponent = mapLegend.layerComponents[0];
                if (currentLayerComponent.legendData.colors && currentLayerComponent.legendData.colors.length > 0) {
                    const colors = currentLayerComponent.legendData.colors;
                    if (colors[0].precipType) {
                        legendColors = [].concat.apply(
                            [],
                            colors.map(x => {
                                return x.colorStops.map(y => {
                                    return y.color;
                                });
                            })
                        );
                        legendLabels = colors.map(x => {
                            return x.precipType;
                        });
                        legendSets = colors;
                    } else {
                        let legendMetadata = colors.find(x => {
                            const formatWindSpeedToMetricnEnglish = windUnit && windUnit >= 1 && windUnit <= 2 ? distanceUnitAbbreviations[windUnit] : distanceUnitAbbreviations[distanceUnits.miles];
                            return typeof x.unitAbbreviation === 'string' && (x.unitAbbreviation.toLowerCase() === temperatureUnitAbbreviations[tempUnit].replace('°', '').toLowerCase()
                                    || x.unitAbbreviation.toLowerCase() === pressureUnitLongAbbreviations[pressureUnit].toLowerCase()
                                    || x.unitAbbreviation.toLowerCase() === lengthUnitsLongAbbreviations[rainUnit].toLowerCase()
                                    || x.unitAbbreviation.toLowerCase() === formatWindSpeedToMetricnEnglish.toLowerCase())
                                    || x.unitType && x.unitType.toLowerCase() === unitSystems.english;
                        });
                        legendLabels = legendMetadata && legendMetadata.colorStops && legendMetadata.colorStops.map(x => {
                            return x.label;
                        });
                        legendColors = legendMetadata && legendMetadata.colorStops && legendMetadata.colorStops.map(x => {
                            return x.color;
                        });

                        legendUnits = legendMetadata && legendMetadata.unitAbbreviation;
                        if (legendUnits) { //TECHDEBT: We going to end up not being able to have legend info for other wind speed unit types i.e Knots & mps, should we do a conversion?
                            if (legendUnits.toLowerCase() === distanceUnitAbbreviations[distanceUnits.miles].toLowerCase()) {
                                legendUnits = speedUnitsEnum[speedUnits.mph];
                            } else if (legendUnits.toLowerCase() === distanceUnitAbbreviations[distanceUnits.kilometers].toLowerCase()) {
                                legendUnits = speedUnitsEnum[speedUnits.kph];
                            }

                        }
                    }
                }
            }
            
            this.setState({
                mapLegend,
                legendLabels,
                legendColors,
                alertsLegend: [],
                legendSets,
                legendUnits
            });   
            
        }
    }

    render() {
        const { controlContainer, zipCode, pollenUrl, airQualityAttributionUrl, showAttributionIcon, infoHeader } = this.props;
        const {
            layers,
            currentLayerId,
            animationTime,
            animationTimeslots,
            currentTimeslotIndex,
            mapMetadata,
            legendColors,
            legendLabels,
            alertsLegend,
            legendSets,
            legendUnits,
            animating,
            isAnimationPaused
        } = this.state;
        const animationEnabled = mapMetadata?.canAnimate;
        const progressPercentage = currentTimeslotIndex / (animationTimeslots.length - 1) * 100;
        const currentPollenUrl = pollenUrl || zipCode && `https://www.pollen.com/allergy-forecast.asp?zip=${zipCode}&affiliateid=6899` || 'https://www.pollen.com/allergy-forecast.asp?affiliateid=6899';

        const layerName = mapMetadata?.layer?.label ?? '';
        const selectedSlotUnixTimeStamp = animating || isAnimationPaused ? animationTimeslots[currentTimeslotIndex]?.time : mapMetadata?.defaultSlot?.time;

        const selectedSlotDate = selectedSlotUnixTimeStamp ? convertUnixTimeStampToDate(selectedSlotUnixTimeStamp * 1000, dateDisplayFormat) : '';
        const selectedSlotTime = selectedSlotUnixTimeStamp ? convertUnixTimeStampToDate(selectedSlotUnixTimeStamp * 1000, dateTimeDisplayFormat) : '';

        return (
            <div>
                {infoHeader && infoHeader(
                    layerName,
                    selectedSlotDate,
                    selectedSlotTime
                )}
                {controlContainer
                    && controlContainer(
                        layers,
                        this.changeLayer,
                        currentLayerId,
                        animationEnabled,
                        animationTime,
                        this.animationClick,
                        progressPercentage,
                        legendColors,
                        legendLabels,
                        alertsLegend,
                        legendSets,
                        legendUnits,
                        animating
                    )}
                <div id='layerAttribution' style={styles.attributionImageContainer}>
                    {(currentLayerId && currentLayerId.toLowerCase()) === constants.layerNames.pollen.toLowerCase() && <a href={currentPollenUrl} target='_blank' rel='noreferrer'><img src={pollenAttributionImage} alt='Pollen attribution' /></a>}
                    {(showAttributionIcon && (currentLayerId?.toLowerCase() === constants.layerNames.airQuality.toLowerCase())) && <a href={airQualityAttributionUrl} target='_blank' rel='noreferrer'><img src={airQualityByAmbeeAttributionImage} height={19} alt='Air quality attribution' /></a>}
                </div>
            </div>
        );
    }
}

const styles = {
    attributionImageContainer: {
        position: 'absolute'
    }
};
